// AdminDeadEndQueue — UPCs encountered by ≥1 family but never
// promoted to the global barcode_identity_corrections table.
//
// Sibling of AdminPanel (which is already past the 1500-line
// extraction tripwire). Reads the dead_end_upc_queue RPC
// (migration 0171) and surfaces every row an admin should
// consider promoting. One-click PROMOTE writes the dominant
// (canonical, brand, name, package size) to the global tier via
// the existing rememberBarcodeCorrection({ isAdmin: true })
// path — same code that any other admin write goes through.
//
// Two row shapes:
//   * has_canonical = true → one or more families bound the UPC.
//     Show the dominant pick + alts; PROMOTE is one click.
//   * has_canonical = false → families saw the UPC and typed a
//     name but never bound it. Show the typed name; offer OFF
//     and Google as investigation links so the admin can mint a
//     canonical via the CANONICALS tab and re-scan.

import { useEffect, useMemo, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { findIngredient } from "../data/ingredients";
import { rememberBarcodeCorrection } from "../lib/barcodeCorrections";

export default function AdminDeadEndQueue({ viewerId }) {
  const [rows, setRows]     = useState(null);    // null = loading, [] = empty
  const [error, setError]   = useState(null);
  const [busy, setBusy]     = useState(null);    // upc currently promoting
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setRows(null);
      setError(null);
      const { data, error: err } = await supabase
        .rpc("dead_end_upc_queue", { p_limit: 100 });
      if (!alive) return;
      if (err) {
        setError(err.message);
        setRows([]);
        return;
      }
      setRows(Array.isArray(data) ? data : []);
    })();
    return () => { alive = false; };
  }, [version]);

  const total = rows?.length ?? 0;
  const withCanonical    = useMemo(() => (rows || []).filter(r => r.has_canonical).length, [rows]);
  const withoutCanonical = total - withCanonical;

  async function promote(row) {
    if (!row.top_canonical_id) return;        // guard — shouldn't reach the button
    setBusy(row.barcode_upc);
    try {
      const { error: err } = await rememberBarcodeCorrection({
        userId: viewerId,
        isAdmin: true,
        barcodeUpc: row.barcode_upc,
        canonicalId: row.top_canonical_id,
        brand: row.top_brand || undefined,
        name:  row.top_name  || undefined,
        packageSizeAmount: row.top_package_size_amount != null
          ? Number(row.top_package_size_amount)
          : undefined,
        packageSizeUnit: row.top_package_size_unit || undefined,
      });
      if (err) {
        // eslint-disable-next-line no-alert
        window.alert(`Promote failed: ${err.message || err}`);
        return;
      }
      // Optimistic — drop the row from the local list. The next
      // RPC call would also drop it (the not_promoted CTE excludes
      // any UPC that exists in barcode_identity_corrections).
      setRows(prev => (prev || []).filter(r => r.barcode_upc !== row.barcode_upc));
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Promote threw: ${e?.message || e}`);
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) {
    return (
      <div style={{ padding: "40px 12px", textAlign: "center", fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, letterSpacing: "0.1em" }}>
        LOADING…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={emptyStyle}>
        {error
          ? `Load failed: ${error}`
          : "No UPCs in the dead-end queue. Either the household corrections table is empty, or every UPC has already been promoted."}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={kickerStyle}>
        {total} UPC{total === 1 ? "" : "S"}
        {" · "}
        {withCanonical} READY TO PROMOTE
        {" · "}
        {withoutCanonical} NEEDS INVESTIGATION
      </div>

      {rows.map(row => (
        <DeadEndRow
          key={row.barcode_upc}
          row={row}
          busy={busy === row.barcode_upc}
          onPromote={() => promote(row)}
        />
      ))}

      <button
        onClick={() => setVersion(v => v + 1)}
        style={refreshBtnStyle}
      >
        REFRESH
      </button>
    </div>
  );
}

function DeadEndRow({ row, busy, onPromote }) {
  const ing = row.top_canonical_id ? findIngredient(row.top_canonical_id) : null;
  const canonicalLabel = ing?.name || row.top_canonical_id || null;

  const lastSeen = row.last_seen_at
    ? new Date(row.last_seen_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" })
    : "—";

  const sizeLabel = row.top_package_size_amount && row.top_package_size_unit
    ? `${row.top_package_size_amount} ${row.top_package_size_unit}`
    : null;

  const alts = Array.isArray(row.alt_canonicals) ? row.alt_canonicals : [];

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 8,
      padding: "12px 14px",
      background: row.has_canonical ? COLOR.ground : COLOR.deep,
      border: `1px solid ${row.has_canonical ? COLOR.edge : COLOR.border}`,
      borderRadius: 12,
    }}>
      {/* Top line — UPC + family count + last seen */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{
          fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
          color: COLOR.ink, letterSpacing: "0.04em",
        }}>
          {row.barcode_upc}
        </span>
        <span style={pillStyle(COLOR.skyDeep, COLOR.sky)}>
          {row.family_count} {row.family_count === 1 ? "FAMILY" : "FAMILIES"}
        </span>
        {row.correction_total > row.family_count && (
          <span style={pillStyle(COLOR.deep, COLOR.muted)}>
            {row.correction_total} OBSERVATIONS
          </span>
        )}
        <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, marginLeft: "auto", letterSpacing: "0.06em" }}>
          {lastSeen.toUpperCase()}
        </span>
      </div>

      {/* Canonical / name body */}
      {row.has_canonical ? (
        <>
          <div style={{
            fontFamily: FONT.serif, fontStyle: "italic", fontSize: 16,
            color: COLOR.ink, lineHeight: 1.25,
          }}>
            {canonicalLabel}
            <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.muted, marginLeft: 8, letterSpacing: "0.08em" }}>
              {row.top_canonical_votes} VOTE{row.top_canonical_votes === 1 ? "" : "S"}
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontFamily: FONT.mono, fontSize: 11, color: COLOR.dim }}>
            {row.top_brand && <span style={pillStyle(COLOR.deep, COLOR.ink)}>BRAND · {row.top_brand}</span>}
            {row.top_name  && <span style={pillStyle(COLOR.deep, COLOR.ink)}>NAME · {row.top_name}</span>}
            {sizeLabel    && <span style={pillStyle(COLOR.deep, COLOR.ink)}>SIZE · {sizeLabel}</span>}
          </div>

          {alts.length > 0 && (
            <div style={altsRowStyle}>
              <span style={{ fontFamily: FONT.mono, fontSize: 9, fontWeight: 700, color: COLOR.muted, letterSpacing: "0.1em" }}>
                ALSO PICKED
              </span>
              {alts.map((a, i) => {
                const aIng = findIngredient(a.canonical_id);
                return (
                  <span key={i} style={pillStyle(COLOR.ground, COLOR.muted)}>
                    {(aIng?.name || a.canonical_id)} · {a.votes}
                  </span>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div style={{
          fontFamily: FONT.serif, fontStyle: "italic", fontSize: 14,
          color: COLOR.muted, lineHeight: 1.35,
        }}>
          {row.top_name
            ? <>No binding yet — families typed <span style={{ color: COLOR.ink }}>"{row.top_name}"</span> and skipped.</>
            : <>No binding, no typed name — household saw this UPC and abandoned the flow.</>}
          {row.top_brand && (
            <span style={{ fontFamily: FONT.mono, fontSize: 11, color: COLOR.dim, marginLeft: 8, letterSpacing: "0.04em" }}>
              · BRAND {row.top_brand}
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        {row.has_canonical ? (
          <button
            onClick={onPromote}
            disabled={busy}
            style={promoteBtnStyle(busy)}
          >
            {busy ? "PROMOTING…" : "PROMOTE TO GLOBAL"}
          </button>
        ) : (
          <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, padding: "6px 0", letterSpacing: "0.06em" }}>
            MINT A CANONICAL VIA THE CANONICALS TAB, THEN RE-SCAN.
          </span>
        )}
        <a
          href={`https://world.openfoodfacts.org/product/${encodeURIComponent(row.barcode_upc)}`}
          target="_blank" rel="noreferrer"
          style={linkBtnStyle}
        >
          OFF →
        </a>
        <a
          href={`https://www.google.com/search?q=${encodeURIComponent(row.barcode_upc)}`}
          target="_blank" rel="noreferrer"
          style={linkBtnStyle}
        >
          GOOGLE →
        </a>
      </div>
    </div>
  );
}

const kickerStyle = {
  fontFamily: FONT.mono, fontSize: 10, fontWeight: 600,
  color: COLOR.muted, letterSpacing: "0.1em",
  padding: "0 4px 4px",
};

const emptyStyle = {
  padding: "28px 16px", textAlign: "center",
  background: COLOR.deep, border: `1px dashed ${COLOR.edge}`, borderRadius: 10,
  fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, fontStyle: "italic",
};

const altsRowStyle = {
  display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
  padding: "6px 8px",
  background: COLOR.deep,
  border: `1px dashed ${COLOR.border}`,
  borderRadius: 8,
};

function pillStyle(bg, fg) {
  return {
    fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
    color: fg,
    background: bg,
    border: `1px solid ${fg}33`,
    padding: "2px 7px",
    borderRadius: 10,
    letterSpacing: "0.08em",
    whiteSpace: "nowrap",
  };
}

function promoteBtnStyle(busy) {
  return {
    padding: "8px 14px",
    background: busy ? COLOR.deep : COLOR.leafDeep,
    border: `1px solid ${COLOR.leafDim}`,
    color: COLOR.leaf,
    borderRadius: 8,
    fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
    letterSpacing: "0.1em",
    cursor: busy ? "wait" : "pointer",
    touchAction: "manipulation",
  };
}

const linkBtnStyle = {
  padding: "8px 12px",
  background: "transparent",
  border: `1px solid ${COLOR.border}`,
  color: COLOR.muted,
  borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 10, fontWeight: 600,
  letterSpacing: "0.08em",
  textDecoration: "none",
  cursor: "pointer",
};

const refreshBtnStyle = {
  marginTop: 8,
  padding: "10px",
  background: COLOR.deep,
  border: `1px solid ${COLOR.edge}`,
  color: COLOR.muted,
  borderRadius: 10,
  fontFamily: FONT.mono, fontSize: 11, fontWeight: 600,
  letterSpacing: "0.1em",
  cursor: "pointer",
  touchAction: "manipulation",
};
